export const CREATIVE_REPORT_ENRICHMENT_MAX_KEYS = 100;
const EXACT_EXPORT_MANIFEST_ID = /^adexport_[a-f0-9]{40}$/;
const EXACT_DEPLOYMENT_BINDING_ID = /^adlocbind_[a-f0-9]{32}$/;
const MEDIA_KINDS = ['image', 'video', 'poster', 'captions', 'transcript', 'audio_track'] as const;
const MAX_MEDIA_PER_MANIFEST = 64;
type CreativeReportMediaKind = typeof MEDIA_KINDS[number];

export type CreativeReportEnrichmentUnavailableReason =
  | 'binding_not_found'
  | 'binding_invalid'
  | 'export_manifest_not_found'
  | 'export_manifest_invalid'
  | 'binding_manifest_mismatch';

export interface CreativeReportEnrichmentBinding {
  id: string;
  projectId: string;
  provider: 'meta' | 'google_ads' | 'tiktok_ads';
  providerAccountId: string;
  decisionUnitKind: 'ad' | 'asset_group' | 'ad_container' | 'creative_asset';
  providerResourceName: string;
  deploymentKey: string;
  providerDeploymentId: string;
  creativeItemId: string;
  exportManifestId: string;
  renditionId: string;
  locale: string;
  localizationVariantId?: string;
  destinationPlacementRef?: string;
  fallbackApproved: boolean;
  exportedManifestHash: string;
  providerPayloadHash: string;
  providerPayloadVersion: string;
}

export interface CreativeReportEnrichmentMedia {
  slotKey: string;
  mediaKind: 'image' | 'video' | 'poster' | 'captions' | 'transcript' | 'audio_track';
  assetId: string;
  url: string;
  smallUrl?: string;
  posterAssetId?: string;
  transcriptAssetId?: string;
  captionsAssetId?: string;
  audioTrackAssetId?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  sha256: string;
  model?: string;
  aspectRatio?: string;
  captions?: {
    artifactId: string;
    url: string;
    sha256: string;
    locale: string;
    cueCount: number;
  };
}

export interface CreativeReportEnrichmentCopyCard {
  cardKey?: string;
  headline?: string;
  body?: string;
  cta?: string;
}

export interface CreativeReportEnrichmentDataUsage {
  sourcePath: string;
  value?: string;
  justification: string;
  category?: string;
  slot?: string;
}

export interface CreativeReportEnrichmentCopy {
  primaryText?: string;
  headline?: string;
  description?: string;
  cta?: string;
  overlayText?: string;
  subtitleScript?: string;
  cardCopy?: CreativeReportEnrichmentCopyCard[];
  dataUsage?: CreativeReportEnrichmentDataUsage[];
  warnings?: string[];
}

export interface CreativeReportEnrichmentDestination {
  type?: 'none' | 'url' | 'article' | 'web_placement' | 'custom';
  url?: string;
  articleId?: string;
  webPlacementAlias?: string;
  label?: string;
}

export interface CreativeReportEnrichmentManifest {
  id: string;
  itemId: string;
  networkId: string;
  creativeType: string;
  templateId?: string;
  formatId?: string;
  locale: string;
  renditionId: string;
  localizationVariantId?: string;
  fallbackApproved: boolean;
  copy: CreativeReportEnrichmentCopy;
  media: CreativeReportEnrichmentMedia[];
  destination: CreativeReportEnrichmentDestination;
  exportedManifestHash: string;
  providerPayloadHash: string;
  providerPayloadVersion: string;
}

export interface CreativeReportEnrichment {
  binding: CreativeReportEnrichmentBinding;
  manifest: CreativeReportEnrichmentManifest;
}

export interface CreativeReportEnrichmentItem {
  deploymentKey: string;
  enrichment: CreativeReportEnrichment | null;
  reason?: CreativeReportEnrichmentUnavailableReason;
}

export interface CreativeReportEnrichmentResult {
  items: CreativeReportEnrichmentItem[];
}

/**
 * Shareable report link for the canonical deployment binding. The report
 * still resolves truth through the deployment key; the locale/rendition/
 * manifest pins make the intended exact export explicit to the reader and to
 * future route handling.
 */
export function buildCreativeRenditionReportHref(
  enrichment: CreativeReportEnrichment,
): string {
  const { binding, manifest } = enrichment;
  const params = new URLSearchParams({
    deployment: binding.deploymentKey,
    locale: manifest.locale,
    rendition: manifest.renditionId,
    exportManifest: manifest.id,
  });
  return `/project/${encodeURIComponent(binding.projectId)}/reports/creative-decisions?${params.toString()}`;
}

/**
 * Read-only handoff intent into Ad Creative. The destination may use these
 * exact immutable export pins to create a NEW Segmently-owned variation; the
 * query explicitly denies provider mutation and never points at Meta source
 * state as an editable object.
 */
export function buildCreativeRenditionVariationHref(
  enrichment: CreativeReportEnrichment,
): string {
  const { binding, manifest } = enrichment;
  const params = new URLSearchParams({
    view: 'library',
    intent: 'start-localized-variation',
    intentVersion: '1',
    sourceKind: 'exact-deployment-export',
    sourceCreativeItem: binding.creativeItemId,
    sourceExportManifest: manifest.id,
    sourceExportedManifestHash: manifest.exportedManifestHash,
    sourceRendition: manifest.renditionId,
    sourceLocale: manifest.locale,
    sourceDeployment: binding.deploymentKey,
    sourceProvider: binding.provider,
    sourceProviderAccount: binding.providerAccountId,
    sourceProviderResource: binding.providerResourceName,
    sourceProviderMutationAllowed: 'false',
    sourceReviewRequired: 'true',
  });
  if (manifest.localizationVariantId) {
    params.set('sourceLocalizationVariant', manifest.localizationVariantId);
  }
  return `/project/${encodeURIComponent(binding.projectId)}/ad-creative?${params.toString()}`;
}

export function parseCreativeReportEnrichmentResult(value: unknown): CreativeReportEnrichmentResult {
  const record = requiredRecord(value, 'creative report enrichment result');
  if (!Array.isArray(record.items) || record.items.length > CREATIVE_REPORT_ENRICHMENT_MAX_KEYS) {
    throw new Error(`Creative report enrichment items must be an array with at most ${CREATIVE_REPORT_ENRICHMENT_MAX_KEYS} entries.`);
  }
  const seen = new Set<string>();
  const items = record.items.map((entry, index): CreativeReportEnrichmentItem => {
    const item = requiredRecord(entry, `items[${index}]`);
    const deploymentKey = requiredString(item.deploymentKey, `items[${index}].deploymentKey`);
    if (seen.has(deploymentKey)) throw new Error(`Duplicate creative report enrichment key: ${deploymentKey}`);
    seen.add(deploymentKey);
    if (item.enrichment === null) {
      return {
        deploymentKey,
        enrichment: null,
        reason: unavailableReason(item.reason, `items[${index}].reason`),
      };
    }
    const enrichment = parseEnrichment(item.enrichment, `items[${index}].enrichment`);
    if (enrichment.binding.deploymentKey !== deploymentKey) {
      throw new Error(`Creative report enrichment deployment identity mismatch for ${deploymentKey}.`);
    }
    return { deploymentKey, enrichment };
  });
  return { items };
}

function parseEnrichment(value: unknown, field: string): CreativeReportEnrichment {
  const record = requiredRecord(value, field);
  const bindingRecord = requiredRecord(record.binding, `${field}.binding`);
  const manifestRecord = requiredRecord(record.manifest, `${field}.manifest`);
  const binding: CreativeReportEnrichmentBinding = {
    id: requiredPattern(bindingRecord.id, EXACT_DEPLOYMENT_BINDING_ID, `${field}.binding.id`),
    projectId: requiredDocumentId(bindingRecord.projectId, `${field}.binding.projectId`),
    provider: oneOf(bindingRecord.provider, ['meta', 'google_ads', 'tiktok_ads'], `${field}.binding.provider`),
    providerAccountId: requiredString(bindingRecord.providerAccountId, `${field}.binding.providerAccountId`),
    decisionUnitKind: oneOf(bindingRecord.decisionUnitKind, ['ad', 'asset_group', 'ad_container', 'creative_asset'], `${field}.binding.decisionUnitKind`),
    providerResourceName: requiredString(bindingRecord.providerResourceName, `${field}.binding.providerResourceName`),
    deploymentKey: requiredString(bindingRecord.deploymentKey, `${field}.binding.deploymentKey`),
    providerDeploymentId: requiredString(bindingRecord.providerDeploymentId, `${field}.binding.providerDeploymentId`),
    creativeItemId: requiredDocumentId(bindingRecord.creativeItemId, `${field}.binding.creativeItemId`),
    exportManifestId: requiredPattern(bindingRecord.exportManifestId, EXACT_EXPORT_MANIFEST_ID, `${field}.binding.exportManifestId`),
    renditionId: requiredString(bindingRecord.renditionId, `${field}.binding.renditionId`),
    locale: requiredString(bindingRecord.locale, `${field}.binding.locale`),
    localizationVariantId: optionalString(bindingRecord.localizationVariantId),
    destinationPlacementRef: optionalString(bindingRecord.destinationPlacementRef),
    fallbackApproved: requiredBoolean(bindingRecord.fallbackApproved, `${field}.binding.fallbackApproved`),
    exportedManifestHash: requiredHash(bindingRecord.exportedManifestHash, `${field}.binding.exportedManifestHash`),
    providerPayloadHash: requiredHash(bindingRecord.providerPayloadHash, `${field}.binding.providerPayloadHash`),
    providerPayloadVersion: requiredString(bindingRecord.providerPayloadVersion, `${field}.binding.providerPayloadVersion`),
  };
  const manifestLocale = requiredString(manifestRecord.locale, `${field}.manifest.locale`);
  const manifest: CreativeReportEnrichmentManifest = {
    id: requiredPattern(manifestRecord.id, EXACT_EXPORT_MANIFEST_ID, `${field}.manifest.id`),
    itemId: requiredDocumentId(manifestRecord.itemId, `${field}.manifest.itemId`),
    networkId: requiredString(manifestRecord.networkId, `${field}.manifest.networkId`),
    creativeType: requiredString(manifestRecord.creativeType, `${field}.manifest.creativeType`),
    templateId: optionalString(manifestRecord.templateId),
    formatId: optionalString(manifestRecord.formatId),
    locale: manifestLocale,
    renditionId: requiredString(manifestRecord.renditionId, `${field}.manifest.renditionId`),
    localizationVariantId: optionalString(manifestRecord.localizationVariantId),
    fallbackApproved: requiredBoolean(manifestRecord.fallbackApproved, `${field}.manifest.fallbackApproved`),
    copy: parseCopy(manifestRecord.copy, `${field}.manifest.copy`),
    media: parseMedia(manifestRecord.media, `${field}.manifest.media`, manifestLocale),
    destination: parseDestination(manifestRecord.destination, `${field}.manifest.destination`),
    exportedManifestHash: requiredHash(manifestRecord.exportedManifestHash, `${field}.manifest.exportedManifestHash`),
    providerPayloadHash: requiredHash(manifestRecord.providerPayloadHash, `${field}.manifest.providerPayloadHash`),
    providerPayloadVersion: requiredString(manifestRecord.providerPayloadVersion, `${field}.manifest.providerPayloadVersion`),
  };
  if (manifest.id !== `adexport_${manifest.exportedManifestHash.slice(0, 40)}`
    || !providerSupportsNetwork(binding.provider, manifest.networkId)
    || binding.exportManifestId !== manifest.id
    || binding.creativeItemId !== manifest.itemId
    || binding.locale !== manifest.locale
    || binding.renditionId !== manifest.renditionId
    || binding.localizationVariantId !== manifest.localizationVariantId
    || binding.fallbackApproved !== manifest.fallbackApproved
    || binding.exportedManifestHash !== manifest.exportedManifestHash
    || binding.providerPayloadHash !== manifest.providerPayloadHash
    || binding.providerPayloadVersion !== manifest.providerPayloadVersion) {
    throw new Error(`Creative report enrichment binding/manifest mismatch at ${field}.`);
  }
  return { binding, manifest };
}

function parseMedia(value: unknown, field: string, manifestLocale: string): CreativeReportEnrichmentMedia[] {
  if (!Array.isArray(value) || value.length > MAX_MEDIA_PER_MANIFEST) {
    throw new Error(`${field} must be an array with at most ${MAX_MEDIA_PER_MANIFEST} entries.`);
  }
  const slotKeys = new Set<string>();
  return value.map((entry, index) => {
    const media = requiredRecord(entry, `${field}[${index}]`);
    const smallUrl = optionalString(media.smallUrl);
    if (smallUrl) requiredCredentialFreeHttpsUrl(smallUrl, `${field}[${index}].smallUrl`);
    const slotKey = requiredString(media.slotKey, `${field}[${index}].slotKey`);
    if (slotKeys.has(slotKey)) throw new Error(`${field} contains duplicate slot ${slotKey}.`);
    slotKeys.add(slotKey);
    const mediaKind = oneOf(media.mediaKind, MEDIA_KINDS, `${field}[${index}].mediaKind`);
    const captions = media.captions === undefined
      ? undefined
      : parseCaptionSidecar(media.captions, `${field}[${index}].captions`);
    const captionsAssetId = optionalDocumentId(media.captionsAssetId, `${field}[${index}].captionsAssetId`);
    const transcriptAssetId = optionalDocumentId(media.transcriptAssetId, `${field}[${index}].transcriptAssetId`);
    if (Boolean(captionsAssetId) !== Boolean(captions)
      || (captionsAssetId && (mediaKind !== 'video'
        || !transcriptAssetId
        || captions?.artifactId !== captionsAssetId
        || captions.locale !== manifestLocale))) {
      throw new Error(`${field}[${index}] caption sidecar does not match its exact export pins.`);
    }
    const mimeType = requiredMediaMimeType(media.mimeType, mediaKind, `${field}[${index}].mimeType`);
    const width = requiresDimensions(mediaKind)
      ? requiredPositiveInteger(media.width, `${field}[${index}].width`)
      : optionalPositiveInteger(media.width, `${field}[${index}].width`);
    const height = requiresDimensions(mediaKind)
      ? requiredPositiveInteger(media.height, `${field}[${index}].height`)
      : optionalPositiveInteger(media.height, `${field}[${index}].height`);
    const durationMs = requiresDuration(mediaKind)
      ? requiredPositiveInteger(media.durationMs, `${field}[${index}].durationMs`)
      : optionalPositiveInteger(media.durationMs, `${field}[${index}].durationMs`);
    return compact({
      slotKey,
      mediaKind,
      assetId: requiredDocumentId(media.assetId, `${field}[${index}].assetId`),
      url: requiredCredentialFreeHttpsUrl(media.url, `${field}[${index}].url`),
      smallUrl,
      posterAssetId: optionalDocumentId(media.posterAssetId, `${field}[${index}].posterAssetId`),
      transcriptAssetId,
      captionsAssetId,
      audioTrackAssetId: optionalDocumentId(media.audioTrackAssetId, `${field}[${index}].audioTrackAssetId`),
      mimeType,
      width,
      height,
      durationMs,
      sha256: requiredHash(media.sha256, `${field}[${index}].sha256`),
      model: optionalString(media.model),
      aspectRatio: optionalString(media.aspectRatio),
      captions,
    });
  });
}

function parseCaptionSidecar(value: unknown, field: string): NonNullable<CreativeReportEnrichmentMedia['captions']> {
  const captions = requiredRecord(value, field);
  return {
    artifactId: requiredPattern(captions.artifactId, /^adcaption_[a-f0-9]{40}$/, `${field}.artifactId`),
    url: requiredCredentialFreeHttpsUrl(captions.url, `${field}.url`),
    sha256: requiredHash(captions.sha256, `${field}.sha256`),
    locale: requiredString(captions.locale, `${field}.locale`),
    cueCount: requiredPositiveInteger(captions.cueCount, `${field}.cueCount`),
  };
}

function parseCopy(value: unknown, field: string): CreativeReportEnrichmentCopy {
  const copy = requiredRecord(value, field);
  const cardCopy = Array.isArray(copy.cardCopy)
    ? copy.cardCopy.map((entry, index) => {
      const card = requiredRecord(entry, `${field}.cardCopy[${index}]`);
      return compact({
        cardKey: optionalString(card.cardKey),
        headline: optionalString(card.headline),
        body: optionalString(card.body),
        cta: optionalString(card.cta),
      });
    })
    : undefined;
  const dataUsage = Array.isArray(copy.dataUsage)
    ? copy.dataUsage.map((entry, index) => {
      const usage = requiredRecord(entry, `${field}.dataUsage[${index}]`);
      return compact({
        sourcePath: requiredString(usage.sourcePath, `${field}.dataUsage[${index}].sourcePath`),
        value: optionalString(usage.value),
        justification: requiredString(usage.justification, `${field}.dataUsage[${index}].justification`),
        category: optionalString(usage.category),
        slot: optionalString(usage.slot),
      });
    })
    : undefined;
  return compact({
    primaryText: optionalString(copy.primaryText),
    headline: optionalString(copy.headline),
    description: optionalString(copy.description),
    cta: optionalString(copy.cta),
    overlayText: optionalString(copy.overlayText),
    subtitleScript: optionalString(copy.subtitleScript),
    cardCopy,
    dataUsage,
    warnings: Array.isArray(copy.warnings) ? copy.warnings.map((entry, index) => requiredString(entry, `${field}.warnings[${index}]`)) : undefined,
  });
}

function parseDestination(value: unknown, field: string): CreativeReportEnrichmentDestination {
  const destination = requiredRecord(value, field);
  const url = optionalString(destination.url);
  if (url) requiredHttpUrl(url, `${field}.url`);
  return compact({
    type: destination.type === undefined
      ? undefined
      : oneOf(destination.type, ['none', 'url', 'article', 'web_placement', 'custom'], `${field}.type`),
    url,
    articleId: optionalString(destination.articleId),
    webPlacementAlias: optionalString(destination.webPlacementAlias),
    label: optionalString(destination.label),
  });
}

function unavailableReason(value: unknown, field: string): CreativeReportEnrichmentUnavailableReason {
  return oneOf(value, [
    'binding_not_found',
    'binding_invalid',
    'export_manifest_not_found',
    'export_manifest_invalid',
    'binding_manifest_mismatch',
  ], field);
}

function requiredRecord(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${field} must be an object.`);
  return value as Record<string, unknown>;
}

function requiredString(value: unknown, field: string): string {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) throw new Error(`${field} is required.`);
  return normalized;
}

function optionalString(value: unknown): string | undefined {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || undefined;
}

function requiredDocumentId(value: unknown, field: string): string {
  const id = requiredString(value, field);
  if (id.includes('/')) throw new Error(`${field} must be a document id.`);
  return id;
}

function optionalDocumentId(value: unknown, field: string): string | undefined {
  const id = optionalString(value);
  if (!id) return undefined;
  if (id.includes('/')) throw new Error(`${field} must be a document id.`);
  return id;
}

function requiredPattern(value: unknown, pattern: RegExp, field: string): string {
  const normalized = requiredString(value, field);
  if (!pattern.test(normalized)) throw new Error(`${field} is invalid.`);
  return normalized;
}

function requiredHash(value: unknown, field: string): string {
  const normalized = requiredString(value, field).toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(normalized)) throw new Error(`${field} must be a SHA-256 hex digest.`);
  return normalized;
}

function requiredBoolean(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`${field} must be a boolean.`);
  return value;
}

function requiredPositiveInteger(value: unknown, field: string): number {
  if (!Number.isInteger(value) || (value as number) <= 0) {
    throw new Error(`${field} must be a positive integer.`);
  }
  return value as number;
}

function optionalPositiveInteger(value: unknown, field: string): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (!Number.isInteger(value) || (value as number) <= 0) {
    throw new Error(`${field} must be a positive integer.`);
  }
  return value as number;
}

function requiredHttpUrl(value: unknown, field: string): string {
  const normalized = requiredString(value, field);
  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    throw new Error(`${field} must be an absolute HTTP(S) URL.`);
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error(`${field} must be an absolute HTTP(S) URL.`);
  }
  return normalized;
}

function requiredCredentialFreeHttpsUrl(value: unknown, field: string): string {
  const normalized = requiredHttpUrl(value, field);
  const parsed = new URL(normalized);
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) {
    throw new Error(`${field} must be a credential-free absolute HTTPS URL.`);
  }
  return normalized;
}

function requiredMediaMimeType(value: unknown, mediaKind: CreativeReportMediaKind, field: string): string {
  const mimeType = requiredString(value, field).toLowerCase();
  const expectedPrefix = mediaKind === 'video'
    ? 'video/'
    : mediaKind === 'audio_track'
      ? 'audio/'
      : mediaKind === 'captions' || mediaKind === 'transcript'
        ? 'text/'
        : 'image/';
  if (!mimeType.startsWith(expectedPrefix) || /[\r\n]/.test(mimeType)) {
    throw new Error(`${field} must match ${mediaKind}.`);
  }
  return mimeType;
}

function requiresDimensions(mediaKind: CreativeReportMediaKind): boolean {
  return mediaKind === 'image' || mediaKind === 'poster' || mediaKind === 'video';
}

function requiresDuration(mediaKind: CreativeReportMediaKind): boolean {
  return mediaKind === 'video' || mediaKind === 'audio_track';
}

function providerSupportsNetwork(
  provider: CreativeReportEnrichmentBinding['provider'],
  networkId: string,
): boolean {
  const normalized = networkId.trim().toLowerCase();
  if (provider === 'meta') return normalized === 'meta';
  if (provider === 'google_ads') return normalized === 'google' || normalized === 'google_ads';
  return normalized === 'tiktok' || normalized === 'tiktok_ads';
}

function oneOf<const T extends readonly string[]>(value: unknown, allowed: T, field: string): T[number] {
  if (typeof value !== 'string' || !allowed.includes(value)) throw new Error(`${field} is invalid.`);
  return value as T[number];
}

function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}
